import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
  type OAuthApi,
} from '@backstage/core-plugin-api';
import { USER_TOKEN_HEADER } from '@platform/plugin-gitlab-ci-common';
import { markGitlabTokenInvalid } from './gitlabTokenStatus';
import type {
  MrApprovals,
  MrBranchList,
  MrCommit,
  MrCompare,
  MrCreateInput,
  MrDetail,
  MrDiffFile,
  MrDiscussion,
  MrDraftNote,
  MrLabel,
  MrListQuery,
  MrMergeInput,
  MrNote,
  MrPosition,
  MrScope,
  MrSummary,
  MrTemplate,
  MrUpdateInput,
  MrUser,
} from '@platform/plugin-gitlab-ci-common';

/** Pipeline rattaché à une merge request. */
export interface MrPipeline {
  id: number;
  /** Identifiant numérique du projet GitLab (routes jobs et logs de `gitlab-ci`). */
  projectId: number;
  status: string;
  ref: string;
  sha: string;
  webUrl: string;
  createdAt: string;
}

/** Erreur renvoyée par le backend (`{ error, code }`), message déjà en français. */
export class MrApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'MrApiError';
    this.status = status;
    this.code = code;
  }
}

export const GITLAB_NOT_CONNECTED = 'GITLAB_NOT_CONNECTED';

/** En-tête de réponse : jeton utilisateur expiré, lecture servie avec le jeton partagé. */
const USER_TOKEN_INVALID_HEADER = 'x-gitlab-user-token-invalid';

export interface MergeRequestsApi {
  config(): Promise<{ host: string }>;
  /** Utilisateur GitLab connecté (401 GITLAB_NOT_CONNECTED sans jeton). */
  me(): Promise<MrUser>;
  list(
    project: string,
    query?: MrListQuery,
  ): Promise<{ items: MrSummary[]; total: number; page: number }>;
  get(project: string, iid: number | string): Promise<MrDetail>;
  diffs(project: string, iid: number | string): Promise<MrDiffFile[]>;
  commits(project: string, iid: number | string): Promise<MrCommit[]>;
  commitDiffs(
    project: string,
    iid: number | string,
    sha: string,
  ): Promise<MrDiffFile[]>;
  discussions(project: string, iid: number | string): Promise<MrDiscussion[]>;
  pipelines(project: string, iid: number | string): Promise<MrPipeline[]>;
  mine(scope: MrScope): Promise<{ items: MrSummary[]; truncated: boolean }>;
  create(project: string, input: MrCreateInput): Promise<MrSummary>;
  update(
    project: string,
    iid: number | string,
    input: MrUpdateInput,
  ): Promise<MrSummary>;
  comment(
    project: string,
    iid: number | string,
    input: { body: string; position?: MrPosition },
  ): Promise<MrDiscussion>;
  reply(
    project: string,
    iid: number | string,
    discussionId: string,
    body: string,
  ): Promise<MrNote>;
  resolve(
    project: string,
    iid: number | string,
    discussionId: string,
    resolved: boolean,
  ): Promise<MrDiscussion>;
  drafts(project: string, iid: number | string): Promise<MrDraftNote[]>;
  createDraft(
    project: string,
    iid: number | string,
    draft: Partial<MrDraftNote>,
  ): Promise<MrDraftNote>;
  updateDraft(
    project: string,
    iid: number | string,
    id: number,
    draft: Partial<MrDraftNote>,
  ): Promise<MrDraftNote>;
  deleteDraft(
    project: string,
    iid: number | string,
    id: number,
  ): Promise<{ ok: true }>;
  /**
   * Publie brouillons, synthèse et approbation. `ok: false` avec `published: true` :
   * brouillons publiés, mais l'étape `step` a échoué (`error`, en français).
   */
  publishReview(
    project: string,
    iid: number | string,
    input: { summary?: string; approve?: boolean },
  ): Promise<PublishReviewResult>;
  approve(project: string, iid: number | string): Promise<{ ok: true }>;
  unapprove(project: string, iid: number | string): Promise<{ ok: true }>;
  merge(
    project: string,
    iid: number | string,
    input?: MrMergeInput,
  ): Promise<MrSummary>;
  members(project: string, search?: string): Promise<MrUser[]>;
  labels(project: string, search?: string): Promise<MrLabel[]>;
  branches(project: string, search?: string): Promise<MrBranchList>;
  compare(project: string, from: string, to: string): Promise<MrCompare>;
  templates(project: string): Promise<MrTemplate[]>;
}

/** Résultat de la publication d'une revue (succès partiel possible). */
export type PublishReviewResult =
  | { ok: true }
  | {
      ok: false;
      published: true;
      step: 'summary' | 'approve';
      error: string;
    };

export const mergeRequestsApiRef = createApiRef<MergeRequestsApi>({
  id: 'plugin.gitlab-ci.merge-requests',
});

export type { MrApprovals };

type Deps = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  gitlabAuthApi: OAuthApi;
};

/** Jeton GitLab de l'utilisateur, sans jamais ouvrir de fenêtre ; '' si indisponible. */
async function silentToken(auth: OAuthApi): Promise<string> {
  try {
    return (await auth.getAccessToken(['api'], { optional: true })) || '';
  } catch {
    return '';
  }
}

export function createMergeRequestsApi(deps: Deps): MergeRequestsApi {
  const { discoveryApi, fetchApi, gitlabAuthApi } = deps;

  async function call<T>(
    method: string,
    path: string,
    opts: {
      query?: Record<string, string | number | undefined>;
      body?: unknown;
    } = {},
  ): Promise<T> {
    const base = await discoveryApi.getBaseUrl('gitlab-ci');
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query ?? {})) {
      if (v !== undefined && v !== '') params.set(k, String(v));
    }
    const qs = params.toString();
    const headers: Record<string, string> = {};
    const token = await silentToken(gitlabAuthApi);
    if (token) headers[USER_TOKEN_HEADER] = token;
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

    let res: Response;
    try {
      res = await fetchApi.fetch(`${base}/mr${path}${qs ? `?${qs}` : ''}`, {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });
    } catch {
      throw new MrApiError(
        0,
        'NETWORK',
        "Impossible de joindre l'IDP. Vérifiez votre connexion.",
      );
    }
    if (!res.ok) {
      let code = 'INTERNAL';
      let message = `Erreur ${res.status} lors de l'appel au serveur.`;
      try {
        const data = await res.json();
        if (typeof data?.code === 'string') code = data.code;
        if (typeof data?.error === 'string') message = data.error;
      } catch {
        // Corps non JSON : message générique.
      }
      // Jeton envoyé mais refusé : la prochaine connexion repartira d'une session neuve.
      if (token && code === GITLAB_NOT_CONNECTED) markGitlabTokenInvalid();
      throw new MrApiError(res.status, code, message);
    }
    if (token && res.headers?.get(USER_TOKEN_INVALID_HEADER) === '1') {
      markGitlabTokenInvalid();
    }
    return (await res.json()) as T;
  }

  const p = (project: string) => `/projects/${encodeURIComponent(project)}`;
  const mrp = (project: string, iid: number | string) =>
    `${p(project)}/merge-requests/${encodeURIComponent(String(iid))}`;
  const e = encodeURIComponent;

  return {
    config: () => call('GET', '/config'),
    me: () => call('GET', '/me'),
    list: (project, query = {}) =>
      call('GET', `${p(project)}/merge-requests`, {
        query: { ...query } as Record<string, string | number | undefined>,
      }),
    get: (project, iid) => call('GET', mrp(project, iid)),
    diffs: (project, iid) => call('GET', `${mrp(project, iid)}/diffs`),
    commits: (project, iid) => call('GET', `${mrp(project, iid)}/commits`),
    commitDiffs: (project, iid, sha) =>
      call('GET', `${mrp(project, iid)}/commits/${e(sha)}/diffs`),
    discussions: (project, iid) =>
      call('GET', `${mrp(project, iid)}/discussions`),
    pipelines: (project, iid) => call('GET', `${mrp(project, iid)}/pipelines`),
    mine: scope => call('GET', '/mine', { query: { scope } }),
    create: (project, input) =>
      call('POST', `${p(project)}/merge-requests`, { body: input }),
    update: (project, iid, input) =>
      call('PUT', mrp(project, iid), { body: input }),
    comment: (project, iid, input) =>
      call('POST', `${mrp(project, iid)}/discussions`, { body: input }),
    reply: (project, iid, did, body) =>
      call('POST', `${mrp(project, iid)}/discussions/${e(did)}/notes`, {
        body: { body },
      }),
    resolve: (project, iid, did, resolved) =>
      call('PUT', `${mrp(project, iid)}/discussions/${e(did)}`, {
        body: { resolved },
      }),
    drafts: (project, iid) => call('GET', `${mrp(project, iid)}/draft-notes`),
    createDraft: (project, iid, draft) =>
      call('POST', `${mrp(project, iid)}/draft-notes`, { body: draft }),
    updateDraft: (project, iid, id, draft) =>
      call('PUT', `${mrp(project, iid)}/draft-notes/${id}`, { body: draft }),
    deleteDraft: (project, iid, id) =>
      call('DELETE', `${mrp(project, iid)}/draft-notes/${id}`),
    publishReview: (project, iid, input) =>
      call('POST', `${mrp(project, iid)}/draft-notes/publish`, { body: input }),
    approve: (project, iid) => call('POST', `${mrp(project, iid)}/approve`),
    unapprove: (project, iid) => call('POST', `${mrp(project, iid)}/unapprove`),
    merge: (project, iid, input = {}) =>
      call('PUT', `${mrp(project, iid)}/merge`, { body: input }),
    members: (project, search) =>
      call('GET', `${p(project)}/members`, { query: { search } }),
    labels: (project, search) =>
      call('GET', `${p(project)}/labels`, { query: { search } }),
    branches: (project, search) =>
      call('GET', `${p(project)}/branches`, { query: { search } }),
    compare: (project, from, to) =>
      call('GET', `${p(project)}/compare`, { query: { from, to } }),
    templates: project => call('GET', `${p(project)}/templates`),
  };
}
